/**
 * types.ts — the wire-format contract between the presentation frontend and
 * any backend (file-replay or live WS).
 *
 * The single most important design rule (inherited from the contextual-bandit
 * engine architecture in `plans/contextual_bandit_engine.md`): **the engine
 * knows nothing about its domain.** No food/kitchen/delivery/CRM references
 * here. A model is a directed graph of nodes connected by edges; a
 * scenario is the graph + metric specs + initial params; a tick is the per-
 * step flows + stocks + (optional) model annotations. Food-delivery is one
 * scenario bundle, not a baked-in assumption.
 *
 * TypeScript is the source of truth. `backend/contract.py` mirrors these
 * types with Pydantic models that must serialise byte-for-byte against this
 * schema — the Phase 0 round-trip test enforces it. If you change anything
 * here, change `contract.py` in the same commit and re-run the test.
 */

// ─── Identifiers ────────────────────────────────────────────────────────────
/** Stable node id within a scenario. Opaque string; the engine never inspects
 *  its contents. */
export type NodeId = string;
/** Stable edge id within a scenario. Conventionally `${source}__${target}` but
 *  the engine treats it as opaque. */
export type EdgeId = string;

// ─── Flow taxonomy ──────────────────────────────────────────────────────────
/**
 * What kind of stuff is moving along an edge. Drives edge colour in the graph
 * view and groups edges into "systems of interactions" (a spec term — the user
 * colours every system differently).
 *
 * This is an open-ish enum: `goods` / `money` / `customers` / `orders` /
 * `signal` cover the common cases, but `custom` lets a scenario name its own
 * (e.g. "ingredients", "calls"). The UI colours by `kind`, falling back to a
 * palette slot for `custom` keyed on `customKind`.
 */
export type FlowKind = 'goods' | 'money' | 'customers' | 'orders' | 'signal' | 'custom';

// ─── Node types ─────────────────────────────────────────────────────────────
/**
 * The role a node plays in the economy. The first three are boundary /
 * accumulation kinds; `actor` and `gate` are the interior processing kinds.
 *
 * - `source` — a boundary that injects flow into the graph. Reads its `rate`
 *              param each tick (optionally noisy via `source.distribution`)
 *              and emits it along out-edges. Does not conserve stock.
 * - `sink`   — a boundary that destroys incoming flow and accumulates it into
 *              `state.consumed` (e.g. fulfilled orders, churned customers).
 * - `buffer` — an accumulation point. Stock is the headline quantity; if
 *              stock exceeds the node's `capacity` param, the excess spills
 *              to `state.overflow` and is held aside (the buffer is "full").
 *              A growing buffer stock IS the mathematical definition of a
 *              bottleneck. Conserves stock.
 * - `actor`  — interior pass-through. A processing unit that passes flow
 *              through (CRM, kitchen, bank). Conserves stock.
 * - `gate`   — interior router/decision. Picks how inflow fans out across
 *              out-edges per `gate.fn`. Conserves stock.
 * - `delay`  — interior pass-through with a transportation delay. Flow entering
 *              is held for `delay.ticks` before becoming available. Conserves stock.
 * - `assembler` — interior multi-commodity transformation. Reads specific flows
 *              and combines them according to `assembler.recipe` to produce outflow.
 *
 * `conserves?: boolean` is honoured when set. Unset, interior nodes default
 * `true` — except `assembler`, which defaults `false` (a recipe consumes N
 * input units per output unit, so the accounting identity mixes units).
 * Pure boundary nodes (no in- or no out-edges) are exempt from the assertion
 * regardless.
 */
export type NodeKind = 'source' | 'sink' | 'buffer' | 'actor' | 'gate' | 'delay' | 'assembler';
/**
 * How a gate decides what to push downstream each tick. Every fn first fixes
 * the TOTAL outflow (capped by the gate's capacity param); `argmax`/`balance`
 * additionally decide WHERE it goes, scoring out-edges by their targets'
 * previous-tick stocks (the same one-tick lag signal edges use, so routing is
 * order-independent and feedback-safe).
 *
 * - `pass`      — push inflow straight to outflow (a wire). Default.
 * - `threshold` — release up to `capacity` per tick; hold the rest as stock.
 * - `argmax`    — route ALL outflow to the single out-edge whose target has
 *                 the least previous-tick stock (ties → higher edge weight).
 * - `balance`   — split outflow across out-edges weighted by
 *                 `weight / (1 + target's previous-tick stock)` — emptier
 *                 targets draw more; equally stocked targets degrade to the
 *                 plain weight split.
 * - `formula`   — outflow = `gate.expr` evaluated as a CEL-compatible
 *                 expression against the node's scope (self.stock, self.inflow,
 *                 self.<paramKey>, plus nodes.X.* / edges.ID.* cross-refs).
 *                 The slot where derived formulas from neural-symbolic agents
 *                 land. Evaluated by the owned walker — no eval, no new Function.
 */
export type GateFn = 'pass' | 'threshold' | 'argmax' | 'balance' | 'formula';

// ─── Parameters ─────────────────────────────────────────────────────────────
/**
 * A declarative association between one graph param and a project table cell.
 * The row is addressed by a key-column lookup (`table` + `keyColumn` + `key`,
 * e.g. `media` where `name = 'instagram'`, column `budget`) — unlike a row id,
 * this survives re-seeds, row re-creation and project import/export.
 *
 * When bound, the DB cell is the SOURCE OF TRUTH for the param's live value:
 * the resolver reads the cell each tick (clamped to the ParamSpec's min/max)
 * and writes it into the runtime param overlay, so user/agent table writes
 * drive the graph. The ParamSpec's saved `value` is demoted to a FALLBACK —
 * used only when the row/cell is missing, empty or non-numeric (the UI marks
 * the binding broken). Slider edits on a bound param write through to the cell.
 */
export interface CellBinding {
  /** Target table name (a project collection, e.g. "media"). */
  table: string;
  /** Column whose value identifies the row (e.g. "name"). */
  keyColumn: string;
  /** The identifying value (string form; e.g. "instagram"). */
  key: string;
  /** Column holding the param's value (must be numeric). */
  column: string;
}

/**
 * A single tunable knob exposed in the node/edge modal or the play config.
 * `value` is always a number — never `number | string` — so the wire format
 * is unambiguous and the slider UI has one type to handle.
 */
export interface ParamSpec {
  /** Machine key, unique within the node/edge's param set. */
  key: string;
  /** Human label shown in the modal. */
  label: string;
  value: number;
  min: number;
  max: number;
  /** Optional step for the slider. Defaults to (max - min) / 100. */
  step?: number;
  /** One-line help text for the nerd tooltip. */
  hint?: string;
  /** Optional association with a project table cell. When set, the DB cell
   *  drives the live value (see CellBinding); `value` above is the fallback. */
  binding?: CellBinding | null;
}

export interface AgentConfig {
  /** Stable identity minted at creation (uuid). Stamped on every decision/log
   *  the agent authors, so the activity log can group by agent. Absent on
   *  older bundles — identity then derives from the attachment point
   *  (`node:<id>` / `edge:<id>` / `global:<target>`). */
  id?: string;
  /** Human display name ("Grid optimizer"). Purely cosmetic; the id is the
   *  identity key. */
  name?: string;
  /** The target the agent invokes: a URL (API mode) or a document/file id
   *  (Code mode). When empty the agent is considered unconfigured. */
  target: string;
  /** HTTP method (API mode) or function name (Code mode). */
  method?: string;
  payload?: string;
  linkedDocumentId?: string;
  inputMapping?: Record<string, string>;
  /** Which knobs the agent is allowed to write — **the capability fence**.
   *  Keys are full param paths (`<nodeId>.<key>` / `e:<edgeId>.<key>` for
   *  local agents; any path for global agents); values are the agent's own
   *  output slot names (currently informational). The runner drops any
   *  `paramDelta` whose path isn't listed here.
   *
   *  **Phase C breaking change:** an empty/absent map now means **read-only
   *  observer** (no writes allowed), not "all". The old unsafe default was
   *  inappropriate for third-party agents. The AgentModal's "All params"
   *  pill enumerates the full key set into this map rather than leaving it
   *  empty. See `docs/AGENT_SYSTEM.md` "The capability fence". */
  outputMapping?: Record<string, string>;
  /** Action-grant fence for non-param actions (docs/TRAINING.md §2). Each
   *  entry is a bare kind (`"emitEvent"` / `"triggerWorkflow"` — any target)
   *  or a specific grant (`"emitEvent:demand_spike"`,
   *  `"triggerWorkflow:settlement"`). Absent/empty grants no events and no
   *  workflow triggers — the conservative default, mirroring empty
   *  outputMapping = read-only. */
  actionGrants?: string[];
  /** Magnitude fence: cap `|value − current|` per knob write, applied after
   *  the declared min/max clamp (agent writes are clamped to each param's
   *  `ParamSpec.min/max`). Absent = unlimited. Each bound applied is
   *  surfaced as a decision annotation in the decisions feed. */
  maxDeltaPerTick?: number;
  /** Which mode the agent runs in. Determines how `target` is interpreted
   *  (URL vs doc id) and which input channel the agent reads. Default 'api'. */
  mode?: 'api' | 'code';
  /** Where the agent is attached. `'local'` (default) = attached to a node or
   *  edge via `node.agentConfig` / `edge.agentConfig`; the agent sees only its
   *  host's scoped data. `'global'` = lives on `bundle.agents[]`; sees the full
   *  tick + params + bundle. See `docs/AGENT_SYSTEM.md` "Local vs global scope". */
  scope?: 'local' | 'global';
}

// ─── Typed agent actions (display mirror of backend/agents/protocol.py) ─────
// The action bus (docs/TRAINING.md §2). Agents execute on the BACKEND; these
// types exist so the frontend can render what an agent did / is granted.

export type AgentAction =
  | { kind: 'setParam'; path: string; value: number }
  | { kind: 'emitEvent'; eventType: string; payload?: Record<string, unknown>; dueTick?: number; agent?: string }
  | { kind: 'triggerWorkflow'; workflow: string; input?: Record<string, unknown>; agent?: string };

// ─── Graph structure ────────────────────────────────────────────────────────

/** Which per-tick quantity the canvas shows as a node's headline number.
 *  The same vocabulary as the chart builder's `NodeFieldQuantity`, plus
 *  `'none'` (hide the headline). Default/absent → `'stock'`. */
export type NodeDisplayValue = 'stock' | 'inflow' | 'outflow' | 'none' | `state:${string}`;

export interface GraphNode {
  id: NodeId;
  /** Display name (e.g. "Kitchen", "Bank"). */
  label: string;
  /** Icon — emoji or a short token the renderer maps to a glyph. Opaque to
   *  the engine; the UI decides how to render. */
  icon: string;
  kind: NodeKind;
  /** Present iff `kind === 'gate'`. Regular actors have no gate.
   *
   *  `expr` carries the CEL expression for `fn: 'formula'` (outflow = its
   *  value, clamped to what's available). `capacityParam` names the node/gate
   *  param key that caps per-tick outflow for every fn except `formula`
   *  (default `'capacity'`; a missing param means uncapped). */
  gate?: { fn: GateFn; params: ParamSpec[]; expr?: string; capacityParam?: string };
  /** Tunable parameters that affect the node's behaviour but aren't the gate
   *  itself (e.g. capacity, lead-time). Shown in the node modal. */
  params: ParamSpec[];
  /** Optional 2D position hint for the renderer. If absent the layout pass
   *  (dagre/elk) computes one. */
  position?: { x: number; y: number };
  /** Free-form grouping for legend / clustering (e.g. "supply", "sales").
   *  Optional and purely cosmetic unless a cluster annotation overrides it. */
  group?: string;
  /** Explicit membership in one or more spatial `NodeGroup`s (by group id).
   *  Independent of spatial containment: a node is a member of a group when
   *  its centre is inside the group's rect OR its id is listed here. This lets
   *  a node belong to a group even when it sits outside the rectangle (out-of-
   *  band assignment). Aggregation dedupes per-group, so a node is counted
   *  once per group it belongs to. Distinct from the cosmetic `group` field
   *  above (legend colour) and from edge `group` (cosmetic edge-system label). */
  groupIds?: string[];
  /** Whether the engine should assert stock conservation (stock(t+1) =
   *  stock(t) + inflow - outflow) at this node. Default: true for interior
   *  nodes (those with both in- and out-edges) — except `assembler` kinds,
   *  which default false (a recipe's inputs and output are different things;
   *  the identity mixes units and never holds). An explicit value always wins.
   *
   *  Set `false` at boundary nodes where inflow and outflow mix units (e.g. a
   *  "customers" node where orders+money flow out and meals flow in to be
   *  consumed — different units, so the accounting identity doesn't apply).
   *  This keeps the engine domain-free: the scenario describes its own
   *  accounting boundary, the engine just honours it. */
  conserves?: boolean;
  /** The stock's unit/dimension, purely declarative ("meals", "$", "kWh").
   *  Documentation + the editor's unit-consistency lint: a conserving
   *  interior node whose physical neighbours declare more than one distinct
   *  unit is mixed-unit flow through a node claiming conservation — mark it
   *  `conserves: false` or split the boundary. The engine never reads it. */
  unit?: string;
  /** Compute-engine metadata for a `source` node. The rate each tick is read
   *  from the node's `rate` param (override the key via `rateParam`); the
   *  optional `distribution` adds noise so a source can model market demand
   *  with random spikes. Ignored for non-source kinds. */
  source?: { rateParam?: string; distribution?: 'constant' | 'uniform' | 'normal'; distParams?: ParamSpec[] };
  /** Compute-engine metadata for a `delay` node. */
  delay?: { ticksParam?: string };
  /** Compute-engine metadata for an `assembler` node. Map of FlowKind (or customKind)
   *  to the amount required to produce 1 unit of outflow. */
  assembler?: { recipe: Record<string, number> };
  agentConfig?: AgentConfig;
  computedMetrics?: ComputedMetric[];
  /** Cosmetic: which per-tick quantity the canvas renders as this node's
   *  headline number (`NodeDisplayValue`). Absent → `stock`. Pure display —
   *  the engine never reads it. Useful where stock is structurally 0 (e.g.
   *  pass-through flow nodes whose meaning lives in their inflow/outflow). */
  displayValue?: NodeDisplayValue | null;
}

export interface ComputedMetric {
  key: string;
  label: string;
  expr: string;
}

/** A spatial node group — a labelled rectangle drawn BEHIND the graph. A node
 *  is a member of a group when its centre point falls inside `rect` (derived at
 *  render time; nothing is stored on the node, no RF parent/child wiring).
 *
 *  Groups are NOT nodes: the sim engine never sees them. They exist purely as
 *  presentation + aggregation containers (finance / production / logistics
 *  style). Each group carries its own `computedMetrics`, evaluated against a
 *  scope whose `self` is the SUM of member-node state (see balance.ts). */
export interface NodeGroup {
  id: string;
  /** Display name (e.g. "Finance", "Production"). */
  label: string;
  /** Rectangle in flow coordinates. */
  rect: { x: number; y: number; w: number; h: number };
  /** Optional fill colour override. When absent the renderer hashes the id. */
  color?: string;
  /** When true, the rect body is filled with the group colour at low opacity;
   *  when false (default) only the dashed outline is drawn. Toggled from the
   *  group's context menu ("Background"). */
  showBackground?: boolean;
  /** Group-level metric exprs. `self` is bound to the summed member state;
   *  `nodes.*` / `edges.*` from the global scope remain available for cross-refs
   *  (e.g. `nodes.bank.stock`). Reuses ComputedMetric so the editor UI matches
   *  the per-node computed-metrics editor. */
  computedMetrics?: ComputedMetric[];
}

export interface GraphEdge {
  id: EdgeId;
  source: NodeId;
  target: NodeId;
  /** React Flow handle ids on the source/target nodes (e.g. 'right-source',
   *  'top-target'). When set, the edge routes through that specific side so a
   *  user-drawn connection from the right stays on the right. The engine
   *  ignores these — it keys edges by node id — they're render-only. */
  sourceHandle?: string;
  targetHandle?: string;
  /** What's flowing. Drives colour + the flow-particle visual. */
  flowKind: FlowKind;
  /** When `flowKind === 'custom'`, the renderer keys colour off this label. */
  customKind?: string;
  /** Explicit colour for a custom flow kind. When set, overrides the hash-based
   *  colour from `customKind` so the user can pin a specific swatch from the
   *  edge modal's colour picker. */
  customColor?: string;
  /** Weight ∈ (0, 1]: what fraction of the source's outflow this edge carries
   *  when there are multiple out-edges (the rest split the remainder). For a
   *  single downstream edge, weight is 1.
   *
   *  The weight is also seeded into the runtime param overlay under the
   *  implicit key `'weight'` (addressable as `e:<edgeId>.weight` — setParam,
   *  knob pins, agent outputMapping, cell bindings all work on it like any
   *  param). `fanOut` reads the overlay value with this field as fallback, so
   *  a bound weight can exceed 1 — the split is normalised over the sibling
   *  out-edges' resolved weights (e.g. binding weights to raw budget dollars
   *  yields a budget-proportional split). */
  weight: number;
  /** Optional cell binding for the implicit `weight` param — identical
   *  semantics to `ParamSpec.binding`, resolved at path `e:<edgeId>.weight`.
   *  Lives on the edge because weight has no ParamSpec of its own. */
  weightBinding?: CellBinding | null;
  /** Whether this edge's flow counts toward conservation. Default true.
   *  When false the edge is display-only on the receiving side: the source
   *  still fans out onto it (animated flow, counted in the source's outflow)
   *  but the target's inflow/stock never receive it, and it is not a
   *  topological dependency — for conceptual couplings (information, alerts)
   *  drawn as a custom-kind flow without breaking the target's accounting. */
  conserves?: boolean;
  /** Free-form group label for the groups panel (hide/show/highlight a system
   *  of interactions). Independent from node.group so an edge can belong to a
   *  different system than its endpoints (e.g. a money edge in the "finance"
   *  group between two "production" nodes). */
  group?: string;
  /** User-authored bend points along the edge (double-click the edge to add).
   *  Each bend applies its `mode` to the curve segment around it. Dragged
   *  freely in canvas coords; the path is rebuilt every render. */
  bends?: Bend[];
  /** Where the weight label sits ALONG the edge's curve, as a t-parameter in
   *  [0, 1] (0 = source endpoint, 1 = target endpoint, 0.5 = middle of the
   *  path's arc length). Drag the label to set it; it slides freely along the
   *  curve, constrained to the path. The renderer resolves t to actual (x, y)
   *  via SVGPathElement.getPointAtLength. Default 0.5. */
  labelT?: number;
  /** Per-edge tunables (e.g. latency, unit cost). Shown in the edge modal. */
  params: ParamSpec[];
  /** The typed param-links this edge carries. Replaces the old single-`weight`
   *  + `derive` model: one canvas arrow can carry N typed connections, each
   *  transformed by a CEL-compatible expression and routed to a specific target
   *  slot. An edge with no `connections` array falls back to weight-based
   *  fan-out (the original behaviour) for backward compat. */
  connections?: EdgeConnection[];
  /** Optional agent bound to this edge. Mirrors `GraphNode.agentConfig`:
   *  schema-only today (no agent runner exists), this is the seam for letting
   *  an external agent read this edge's connections as input and write its
   *  params as output. */
  agentConfig?: AgentConfig;
  /** @deprecated migrated to one `connections` entry at load time. Kept so the
   *  existing food_delivery graph still loads before Layer 3. */
  derive?: { fromEdge?: string; priceParam?: string; rateParam?: string };
}

/**
 * One typed link along an edge: a flow of resource (rate / derive) or a
 * read-only state exposure (signal). Each connection has its own CEL
 * transform so a single edge can both move goods AND carry the resulting
 * money (one `derive` connection priced by an edge param), or expose a
 * downstream buffer's state to an upstream gate (one `signal` connection).
 *
 * - `rate`   — a fixed per-tick flow. `sourceParam` names the source node's
 *              param key (default 'rate'); the value is transformed by `expr`
 *              (default 'x' = identity) and added to the target's `targetField`.
 * - `derive` — flow derived from another edge's flow. `sourceParam` is the
 *              source EDGE id; `expr` typically multiplies by a price param:
 *              `edges.X.flow * params.price` (the edge's own params are in scope
 *              as `params.<key>`). This subsumes the old `derive.fromEdge` +
 *              `priceParam` pair.
 * - `signal` — read-only state exposure (no flow). `sourceParam` is the source
 *              node's state key (or 'stock'/'inflow'/'outflow'); the value is
 *              placed into the target node's formula scope under the
 *              `signal` map. Lets a gate read downstream state without creating
 *              a resource-flow cycle.
 */
export interface EdgeConnection {
  kind: 'rate' | 'derive' | 'signal';
  /** CEL-compatible transform. Default 'x' (identity). The source value is
   *  bound to `x` in scope; the edge's own params are bound as `params.<key>`;
   *  the source/target node params are `source.<key>` / `target.<key>`. */
  expr?: string;
  /** What this connection reads from. For `rate`: a source-node param key
   *  (default 'rate'). For `derive`: a source edge id. For `signal`: a source
   *  node state key (or 'stock'/'inflow'/'outflow'). */
  sourceParam?: string;
  /** Where this connection writes (resource kinds only). 'inflow' (default) or
   *  a named state key on the target node. Signal connections ignore this. */
  targetField?: string;
}

export interface Graph {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

/** A user-authored bend point on an edge. Dragged freely in canvas coords; the
 *  edge's path is rebuilt every render via bendPath. The `mode` controls how
 *  the curve passes through this point:
 *  - 'sharp'  — a straight-cornered polyline through the point (no rounding).
 *               This is the default for newly-created bends.
 *  - 'smooth' — a rounded corner cut by a quadratic bezier at the bend point.
 *
 *  `'default'` is retained as a legacy alias for `'sharp'` (older scenarios +
 *  bend data may carry it); bendPath treats any non-`smooth` mode as sharp.
 *  The context menu no longer offers 'default' as a choice.
 *
 *  The mode applies to the segment around the bend, so mixing modes on a
 *  multi-bend edge produces a curve with different regional characters. */
export interface Bend {
  x: number;
  y: number;
  mode: 'sharp' | 'smooth' | 'default';
}

// ─── Metrics ────────────────────────────────────────────────────────────────
/**
 * How a metric is rolled up from per-node quantities. The engine computes the
 * per-node numbers; the metric spec says how to combine them into the headline
 * number on the sidebar card.
 *
 * Which per-node number a metric aggregates over.
 *
 * - `inflow` / `outflow` / `stock` — the three conservation quantities the
 *   engine tracks for every node.
 * - `param:<key>` — the current value of a node parameter (e.g. a kitchen's
 *   "capacity" param). Lets metrics depend on knobs the user twiddles.
 * - `state:<key>` — a per-node state field. The engine populates these for the
 *   new node kinds: `state:consumed` (sink cumulative inflow), `state:overflow`
 *   (buffer excess above capacity). Custom state can be set by a `formula` gate.
 * - `edge:<edgeId>.flow` — the current flow on a specific edge (so a metric
 *   can aggregate, say, total meals delivered by summing a delivery edge).
 * - `edge:<edgeId>.<paramKey>` — the current value of an edge parameter.
 */
export type NodeQuantity =
  | 'inflow'
  | 'outflow'
  | 'stock'
  | `param:${string}`
  | `state:${string}`
  | `edge:${string}.flow`
  | `edge:${string}.${string}`;

export interface MetricSpec {
  /** Machine key (e.g. "roi", "turnover", "fulfilment"). */
  key: string;
  /** Human label on the sidebar card. */
  label: string;
  /** Unit suffix (e.g. "$", "%", "units"). */
  unit?: string;
  /** Higher is better (▲ green) vs lower is better (▼ red). */
  direction: 'up' | 'down';
  /** CEL expression computing the metric value. */
  expr: string;
  /** Restrict the aggregation to nodes in this group. Omit = all nodes. */
  group?: string;
  /** When set, this is a PER-NODE metric: it computes only for this node and
   *  shows in the node's modal + the sidebar's per-node section. Omit (or
   *  null) for a global metric aggregated across the graph. */
  nodeId?: NodeId | null;
}

// ─── Tick payload (what the backend pushes per step) ────────────────────────
/** Per-node conservation quantities for one tick. stock(t+1) = stock(t) +
 *  inflow - outflow, asserted by the engine every step. */
export interface NodeValues {
  inflow: number;
  outflow: number;
  stock: number;
  /** Free-form model state (backlog, queue depth, whatever the model tracks).
   *  Optional; the engine carries it but doesn't interpret it. */
  state?: Record<string, number>;
}

/**
 * Model projections for this tick. These are what make the demo valuable to a
 * modeller — without them it's a generic flow engine. Each kind maps
 * to a visual cue in `overlays/annotations.ts`.
 */
/** A cross-reference from a decision to another project entity. Currently
 *  only workflows (an agent-triggered run); `kind` discriminates future
 *  entity types. */
export interface DecisionRef {
  kind: 'workflow';
  /** Workflow name (the `<project>__<name>` registry key, unprefixed name). */
  workflow: string;
  /** The started run's id, when known. */
  runId?: string | null;
}

/** One agent/model decision for the activity log. `agent` is the authoring
 *  agent's identity key (AgentConfig.id or the derived attachment id);
 *  absent = the model itself authored it (external annotations). `node` is
 *  optional since some decisions are node-less (e.g. an agent-triggered
 *  workflow from a global agent). */
export interface Decision {
  node?: NodeId;
  action: string;
  rationale?: string;
  agent?: string;
  refs?: DecisionRef[];
}

/** Debug/log lines agents emit alongside their decisions. The runner stamps
 *  `agent` (authoritative identity — agents can't spoof it); `level` is the
 *  usual severity ladder; `data` is an opaque structured payload. */
export interface AgentLog {
  agent: string;
  level: 'debug' | 'info' | 'warn';
  message: string;
  data?: unknown;
}

export interface Annotations {
  /** Nodes that are choking throughput → shrink node, slow out-edge flow. */
  bottlenecks?: NodeId[];
  /** Isolated subgraphs the model flagged → coloured hull + label. */
  anomalies?: { nodes: NodeId[]; label?: string }[];
  /** Discovered relationship clusters → recolour nodes. */
  clusters?: { nodes: NodeId[]; color: string }[];
  /** Decisions the agent made this tick → toast + side-panel entry. */
  decisions?: Decision[];
  /** Agent debug logs for this tick (docs/TRAINING.md §2) — accumulate the
   *  reasoning that PRECEDES a decision. Capped per tick server-side. */
  agentLogs?: AgentLog[];
}

export interface EventPayload {
  id: string;
  type: string;
  timestamp: number;
  data: Record<string, any>;
  /** Schedules the event at TICK granularity: absent/0 = due immediately
   *  (drained by the model's next tick); N > 0 = invisible to the model until
   *  its tick counter reaches N (the drain filter is
   *  `processed = 0 AND dueTick <= step`). Sub-tick dynamics remain
   *  tick-resolution. */
  dueTick?: number;
}

export interface TickPayload {
  /** Monotonic step counter. The engine drops any tick with step ≤ last
   * applied (defence against out-of-order WS delivery). */
  step: number;
  /** The graph this tick targets/originated from — the normalized graph file
   * name, i.e. `ScenarioBundle.id` (`graphs/<id>.json`). Absent → Main. An
   * external model posts a graph-SCOPED event (only that graph's values); the
   * server validates the values against that graph, runs only that graph's
   * agents, and broadcasts the project-merged view carrying this origin id.
   * The in-browser co-tick engine omits it (its ticks are always merged). */
  graphId?: string | null;
  nodeValues: Record<NodeId, NodeValues>;
  /** Current flow magnitude per edge (drives the animated dots). */
  edgeFlows: Record<EdgeId, number>;
  /** Pre-computed metric values for this tick. Present when any metric uses
   *  the `expr` rollup (the engine can't compute those); otherwise the
   *  engine derives them from `nodeValues` via the `MetricSpec`s. */
  metricValues?: Record<string, number>;
  /** Effective param (knob) values this tick, including agent deltas, keyed by
   *  the setParam path grammar (`<nodeId>.<key>` / `e:<edgeId>.<key>`). Optional
   *  — populated by adapters that expose knobs; when absent the chart builder's
   *  Knobs field group is hidden. Lets charts plot knob changes over time. */
  params?: Record<string, number>;
  annotations?: Annotations;
}

// ─── Scenario bundle (what `loadScenario` returns) ──────────────────────────
/**
 * The complete definition of a runnable scenario. This is what the composer
 * produces, what a `graph.json` file contains, and what the WS backend sends
 * on connect. The frontend treats it as immutable after load — knob changes
 * go through `setParam`, not by mutating the bundle.
 */
/** A user-editable legend entry — a colour swatch + its label, rendered in the
 *  sidebar so the graph's edge colours are legible. Persisted on the bundle so
 *  the legend is per-project, not a global default. Until the user edits it,
 *  the sidebar derives entries from the palette (`flowLegend`); once edited,
 *  the saved list is authoritative. */
export interface LegendEntry {
  label: string;
  color: string;
}

/** A pinned knob on the sidebar's "Knobs" section. Two sources:
 *  - `graph` pins reference a node/edge `ParamSpec` by its `setParam` path
 *    (`<nodeId>.<key>` / `e:<edgeId>.<key>`). Range + label resolve live from
 *    the underlying param, so they stay in sync when the param is edited.
 *  - `db` pins reference a numeric cell in a project table. The range is
 *    user-supplied (db cells have no inherent min/max); sliding writes the
 *    cell via the tables PATCH route. Only meaningful when a backend is wired.
 *
 *  Each pin carries a stable `id` (uuid) so reorder / delete survives label or
 *  path changes. Cosmetic metadata — does not affect the sim directly; the
 *  slider drags call `setParam` (graph) or `updateTableCell` (db). */
export interface GraphKnobPin {
  id: string;
  type: 'graph';
  /** `setParam` path: `<nodeId>.<key>` or `e:<edgeId>.<key>`. */
  path: string;
  /** Optional display override; falls back to the underlying ParamSpec.label. */
  label?: string;
}

export interface DbKnobPin {
  id: string;
  type: 'db';
  label: string;
  table: string;
  /** The target row. Numeric for rows the app mints; a string when the
   *  storage backend mints uuid ids (e.g. events records). */
  rowId: number | string;
  /** Must reference a numeric column (integer / float). */
  column: string;
  min: number;
  max: number;
  step?: number;
}

export type KnobPin = GraphKnobPin | DbKnobPin;

export interface ScenarioBundle {
  id: string;
  label: string;
  description?: string;
  graph: Graph;
  metrics: MetricSpec[];
  /** Initial param values, keyed by node id then edge id (edges prefixed with
   *  `e:` to disambiguate from node ids). The engine seeds its runtime param
   *  store from this; missing entries fall back to the ParamSpec defaults in
   *  the graph. */
  initialParams: Record<string, ParamSpec>;
  /** Optional initial stocks (default: every node starts at stock=0). */
  initialStocks?: Record<NodeId, number>;
  /** Known group labels across the bundle — the autocomplete source for the
   *  edge modal's "[+ group]" input. Maintained as edges get grouped; the
   *  groups panel reads the distinct set actually in use + this list.
   *
   *  NOTE: this is the edge-grouping concept (cosmetic, for the groups panel),
   *  distinct from `nodeGroups` below which are spatial node containers. */
  groups?: string[];
  /** Spatial node groups — labelled rectangles drawn behind the graph. A node
   *  belongs to a group when its centre is inside the group's rect. Each group
   *  has its own aggregated balance sheet + computed metrics. */
  nodeGroups?: NodeGroup[];
  /** Timestamp indicating when the user last manually edited the layout.
   *  If null or omitted, the UI applies automatic edge routing. */
  updatedAt?: number;
  /** Global agents — agents attached to the whole bundle rather than to a
   *  single node/edge. Each sees the full tick + params + bundle. The runner
   *  executes these after all local (node/edge) agents on each tick. Entries
   *  should carry `scope: 'global'`; the runner treats any entry here as global
   *  regardless. See `docs/AGENT_SYSTEM.md`. */
  agents?: AgentConfig[];
  /** The legend shown in the sidebar. When absent, the sidebar derives entries
   *  from the palette (`flowLegend`); once the user edits the legend, this list
   *  is the per-project source of truth. Cosmetic — does not affect the sim. */
  legend?: LegendEntry[];
  /** Pinned knobs shown in the sidebar's "Knobs" section. Each entry references
   *  either a graph param (by setParam path) or a numeric cell in a project
   *  table. Empty list stored as `undefined` so untouched bundles show nothing.
   *  Cosmetic metadata — slider drags dispatch through `setParam` / table PATCH. */
  knobs?: KnobPin[];
  /** Signal→workflow triggers (the trigger contract): declarative links that
   *  start a durable workflow run when a condition over the tick holds. The
   *  backend's relay path evaluates `on:'expr'` triggers every tick (rising
   *  edge + cooldown); `sourceNode`/`sourceAgent` are UI attribution — the
   *  canvas badges the node, the Workflows rail shows the link. Agents may
   *  ALSO trigger workflows at runtime through the action bus's
   *  `triggerWorkflow` action (gated by `AgentConfig.actionGrants`) — this
   *  list is the declarative, visible wiring. Empty list stored as
   *  `undefined` so untouched bundles show nothing. */
  workflowTriggers?: WorkflowTrigger[];
}

/** One declarative signal→workflow link (docs/TRAINING.md §7a). The expr
 *  vocabulary is the CEL subset over the tick payload: `nodes.<id>.stock`,
 *  `.inflow`, `.outflow`, `.state.<k>`, `edges.<id>.flow`, `metrics.<k>`,
 *  `params.<path>`, `step`. */
export interface WorkflowTrigger {
  id: string;
  /** Target workflow — the module stem under `workflows/` (the backend
   *  re-keys it under `<project>__<name>` on the platform). */
  workflow: string;
  /** `'expr'` auto-fires on the condition (rising edge + cooldown);
   *  `'manual'` documents the link only (the UI/chat fires it);
   *  `'cron'` fires on a wall-clock schedule — the platform's cron service
   *  calls the project's trigger hook (`schedule`), kept in sync by the
   *  backend's cron reconcile. */
  on: 'expr' | 'manual' | 'cron';
  /** The CEL condition for `on:'expr'` triggers. */
  expr?: string;
  /** The 5-field cron expression for `on:'cron'` triggers. */
  schedule?: string;
  /** Human label shown in the UI. */
  label?: string;
  /** Attribution: the graph node this trigger "belongs to" (canvas badge +
   *  NodeModal section). */
  sourceNode?: string;
  /** Attribution: the agent (AgentConfig.id) this trigger belongs to. */
  sourceAgent?: string;
  /** Minimum ticks between firings while the condition keeps holding
   * (default 96 — the backend's `_DEFAULT_TRIGGER_COOLDOWN`). */
  cooldownTicks?: number;
  /** Run input handed to the workflow (a JSON list, matching the trigger
   * route's `{input: [...]}` body). */
  input?: unknown[];
  /** Armed state. Absent/true = the relay path evaluates + fires it;
   * false = kept in the bundle but skipped (the UI's arm/disarm toggle —
   * pause a trigger without deleting its wiring). */
  enabled?: boolean;
}

/** A project is a collection of named graphs. Each entry is a full
 *  `ScenarioBundle` (own `graph`, `metrics`, `initialParams`, `agents`, …) —
 *  persisted as `graphs/<id>.json`, one file per graph. The graph with
 *  `id === 'main'` (label `"Main"`) is the primary, non-removable graph.
 *
 *  The sim co-ticks every graph each step into ONE merged `TickPayload`; node /
 *  edge / metric-key / param-path ids are globally unique across the project so
 *  any metric formula or chart `FieldRef` resolves unambiguously even when the
 *  target lives in another graph. `loadScenario()` returns this; the WS
 *  handshake delivers it as `{kind:'project'}`. */
export interface ProjectBundle {
  graphs: ScenarioBundle[];
}

/** The id of the primary, non-removable graph. */
export const MAIN_GRAPH_ID = 'main';

// ─── Wire format (frontend ↔ API backend over WebSocket) ───────────────────
// The contract any API backend must implement. The FastAPI backend
// (backend/server.py) is the reference implementation; a backend only
// needs to speak these message shapes.
//
// Connection lifecycle:
//   1. Client opens WS to the backend URL, API key in a `?key=` query param.
//   2. Server validates the key, then immediately sends `{kind:'bundle', ...}`.
//   3. Client sends `{type:'start'}` to begin the tick stream; ticks arrive as
//      `{kind:'tick', ...}` at the negotiated rate.
//   4. Editor ops ride as `{type:'edit', op, args, reqId?}`; the server applies
//      them to its LocalAdapter-equivalent and broadcasts `{kind:'bundleUpdate'}`
//      to every client in the same scenario. Ops that return a value carry a
//      `reqId` and get a `{kind:'result', reqId, result}` reply.

/** A knob change. `path` is `<nodeId>` | `<nodeId>.<paramKey>` |
 *  `e:<edgeId>.<paramKey>`. */
export interface SetParamMessage {
  type: 'setParam';
  path: string;
  value: number;
}
/** Start / stop the tick stream. */
export interface ControlMessage {
  type: 'start' | 'stop';
}
/** Reset the sim to step 0 (reseed stocks from initialStocks). */
export interface ResetMessage {
  type: 'reset';
}
/** The full list of editor-op names that can ride the wire as `{type:'edit'}`.
 *  Mirrors `LocalAdapter`'s editor surface 1:1 — the server dispatches each to
 *  the matching method, so adding a new editor op is a one-line change in both
 *  `ApiAdapter` and the server's edit-op table. */
export type EditOpName =
  | 'addNode' | 'updateNode' | 'removeNode' | 'duplicateNode' | 'renameNode'
  | 'addEdge' | 'updateEdge' | 'removeEdge' | 'renameEdge' | 'setEdgeGroup'
  | 'updateEdgeHandles' | 'setBends' | 'setLabelT' | 'addGroup'
  | 'addMetric' | 'updateMetric' | 'removeMetric' | 'renameMetric'
  | 'setParamKey' | 'replaceBundle' | 'exportBundle'
  | 'addNodeGroup' | 'updateNodeGroup' | 'removeNodeGroup' | 'renameGroup'
  | 'setLegend'
  | 'setKnobs'
  | 'setWorkflowTriggers'
  // graph-collection ops (operate on the whole project, not one graph)
  | 'addGraph' | 'removeGraph' | 'renameGraph';

/** The graph-collection ops — they mutate `ProjectBundle.graphs` rather than a
 *  single graph, so the dispatch routes them through a separate path (the
 *  server's `apply_graph_edit` / the frontend's collection handler). `removeGraph`
 *  refuses `MAIN_GRAPH_ID`. */
export const GRAPH_COLLECTION_OPS: ReadonlySet<EditOpName> = new Set([
  'addGraph',
  'removeGraph',
  'renameGraph',
]);

/** An editor op shipped over the wire. `args` are the op's positional args,
 *  JSON-serialized. `graphId` names the target graph for per-graph ops (omitted
 *  → the active / Main graph); ignored for graph-collection ops. `reqId` is set
 *  only for ops that return a value (duplicateNode, renameNode, exportBundle);
 *  the server replies with `{kind:'result', reqId, result}`. */
export interface EditMessage {
  type: 'edit';
  op: EditOpName;
  args: unknown[];
  graphId?: string;
  reqId?: number;
}
/** Request the scenario list (response is `{kind:'list', ids}`). */
export interface ListScenariosMessage {
  type: 'listScenarios';
}
/** Switch the client to a different scenario mid-session. Server replies with
 *  a fresh `{kind:'bundle'}`. */
export interface LoadScenarioMessage {
  type: 'loadScenario';
  id: string;
}
export type ClientMessage =
  | SetParamMessage
  | ControlMessage
  | ResetMessage
  | EditMessage
  | ListScenariosMessage
  | LoadScenarioMessage;

/** Lightweight scenario metadata for the list response. Matches what the
 *  Composer uses to render cards. */
export interface ScenarioMeta {
  id: string;
  label: string;
  description: string;
  editOnly?: boolean;
}

// ─── Downstream messages (backend → frontend over WS) ──────────────────────
export interface BundleMessage {
  kind: 'bundle';
  bundle: ScenarioBundle;
}
/** The full project (all graphs) — sent on WS connect (supersedes the legacy
 *  single-graph `{kind:'bundle'}` handshake). The client switches the active
 *  graph locally; co-ticking happens over the merged tick stream. */
export interface ProjectMessage {
  kind: 'project';
  graphs: ScenarioBundle[];
}
export interface TickMessage {
  kind: 'tick';
  tick: TickPayload;
}
/** The bundle changed (after an edit landed); client re-derives state. */
export interface BundleUpdateMessage {
  kind: 'bundleUpdate';
  bundle: ScenarioBundle;
}
/** One graph's bundle changed (multi-graph edit landed). `graphId` names which;
 *  `bundle` is its fresh full `ScenarioBundle`. The client swaps that entry in
 *  its `ProjectBundle.graphs` and re-derives state if it was the active graph. */
export interface GraphUpdateMessage {
  kind: 'graphUpdate';
  graphId: string;
  bundle: ScenarioBundle;
}
/** A graph was added to the project. `bundle` is the fresh (possibly empty)
 *  `ScenarioBundle`. The client appends it to its `ProjectBundle.graphs`. */
export interface GraphAddedMessage {
  kind: 'graphAdded';
  graphId: string;
  bundle: ScenarioBundle;
}
/** A graph was removed from the project (never `MAIN_GRAPH_ID`). The client
 *  drops it from its `ProjectBundle.graphs`; if it was active, falls back to
 *  Main. */
export interface GraphRemovedMessage {
  kind: 'graphRemoved';
  graphId: string;
}
/** Engine error (CycleError / ExprError) mid-tick. */
export interface ErrorMessage {
  kind: 'error';
  message: string;
}
/** Response to `{type:'listScenarios'}`. */
export interface ListMessage {
  kind: 'list';
  ids: ScenarioMeta[];
}
/** Ack for a `{type:'reset'}`. */
export interface ResetOkMessage {
  kind: 'resetOk';
}
/** Reply to a value-returning edit op. `result` is the op's return value. */
export interface ResultMessage {
  kind: 'result';
  reqId: number;
  result: unknown;
}
/** A knob change broadcast. The Python backend publishes this whenever a
 *  `setParam` lands OR an agent's fenced `paramDelta` is applied — the closed
 *  control loop's feedback half. External models in relay mode read this to
 *  drive their next tick; other tabs apply it to keep their UI in sync.
 *
 *  `path` matches `SetParamMessage.path` (`<nodeId>.<key>` / `e:<edgeId>.<key>`).
 *  Added in Phase D — the Python server was already broadcasting this, but
 *  the TS union silently ignored it; the ApiAdapter now surfaces it via
 *  `onParamChange`. */
export interface ParamChangeMessage {
  kind: 'paramChange';
  path: string;
  value: number;
}
/** A state snapshot sent to a freshly-connected client so it can hydrate the
 *  current run position. Sent right after `{kind:'bundle'}` on the relay path
 *  when the backend has accumulated state (a client connecting mid-run would
 *  otherwise fail conservation on its first tick: its prev is 0 while the
 *  backend's prev reflects N prior ticks). The client adopts `stocks` + `step`
 *  as its baseline; subsequent ticks verify conservation against this. */
export interface StateMessage {
  kind: 'state';
  /** Current step counter (the next tick will be step+1). */
  step: number;
  /** Current stock per node — the conservation baseline for the next tick. */
  stocks: Record<NodeId, number>;
}
/** A workflow step event from the debugger gate, broadcast to the project's
 *  subscribers. Emitted by the worker subprocess's `execute_step` wrapper and
 *  fanned out through the same ConnectionManager as ticks — the Workflows tab
 *  merges these with the platform's event history for live step I/O. The
 *  platform's own events (step_started/completed/...) are NOT re-broadcast;
 *  they're fetched (and polled) via the runs REST routes. */
export interface WorkflowEventMessage {
  kind: 'workflowEvent';
  /** Bare workflow name (module stem). */
  workflow: string;
  runId: string;
  stepId: string;
  /** input | output | error — which side of the step the payload captures. */
  phase: string;
  /** JSON-truncated capture (~8 KB cap; `__truncated` marks a cut). */
  payload?: unknown;
  ts?: number | null;
}

/** A declarative ⚡ trigger (or an agent's `triggerWorkflow` grant) fired or
 * failed to start its workflow — pushed by the relay path so the
 * TriggersPanel / Workflows rail / node badge show live trigger state. A
 * failed fire carries `error` (e.g. "worker not running", "workflow not
 * registered on the platform"); a successful one carries the run id. */
export interface TriggerEventMessage {
  kind: 'triggerEvent';
  /** Bundle trigger id, or `agent:<workflow>` for the runtime grant path. */
  triggerId: string;
  workflow: string;
  /** fired | failed */
  status: string;
  step: number;
  runId?: string | null;
  error?: string | null;
  ts?: number;
}
export type ServerMessage =
  | BundleMessage
  | ProjectMessage
  | TickMessage
  | BundleUpdateMessage
  | GraphUpdateMessage
  | GraphAddedMessage
  | GraphRemovedMessage
  | ErrorMessage
  | ListMessage
  | ResetOkMessage
  | ResultMessage
  | ParamChangeMessage
  | StateMessage
  | WorkflowEventMessage
  | TriggerEventMessage;

// ─── Charts (no-code chart builder) ──────────────────────────────────────────
/**
 * Declarative chart specs for the Plot tab's no-code builder. A user picks a
 * chart type + field mappings (X/Y/color/size) from dropdowns; the frontend's
 * `buildChart` (`src/plot/builder.ts`) turns the spec + the in-memory
 * `recentTicks` into a Vega-Lite (or full-Vega) `PlotSpec` — no Python, no
 * subprocess, no network. Code plots (`plots/*.py`) remain the escape hatch for
 * anything the declarative recipes can't express.
 *
 * The spec is persisted as `charts/<id>.json` (a project file, like dashboards)
 * and indexed by `GET /api/projects/{id}/charts`. `backend/contract.py` mirrors
 * these for Phase-2 EDA report generation (a Python builder) + the round-trip
 * parity tests.
 */

/** The per-node scalar a chart field addresses. A subset of `NodeQuantity` —
 *  the `param:` / `edge:` prefixes are separate FieldRef kinds (params and
 *  edges have their own picker groups in the editor). Modeled as an open
 *  string so the `state:<k>` suffix carries any model-authored state key. */
export type NodeFieldQuantity = 'stock' | 'inflow' | 'outflow' | `state:${string}`;

/** Addresses one plottable quantity in a tick history. The discriminated union
 *  lets the field picker group options (Time / Nodes / Edges / Metrics / Knobs
 *  / Categories / Events) and the builder resolve a value per tick. The
 *  `column` kind addresses a named column of a query/view result row instead
 *  (SQL- and view-datasource charts — see ChartSpec.dataSource). */
export type FieldRef =
  | { kind: 'step' }
  | { kind: 'node'; nodeId: NodeId; quantity: NodeFieldQuantity }
  | { kind: 'edge'; edgeId: EdgeId }
  | { kind: 'metric'; key: string }
  | { kind: 'param'; path: string }
  | { kind: 'nodeAttr'; attr: 'kind' | 'group' | 'groupIds' }
  | { kind: 'edgeAttr'; attr: 'flowKind' | 'group' | 'customKind' }
  | { kind: 'metricAttr'; attr: 'direction' | 'unit' | 'group' }
  | { kind: 'annotation'; anno: 'bottleneck' | 'anomaly' | 'cluster' | 'decision'; field?: string }
  | { kind: 'column'; name: string };

/** The chart types the no-code builder supports. Each maps to a recipe in
 *  `src/plot/builder.ts`. Vega-Lite covers ~20 natively (its mark+encoding+
 *  transform grammar is a 1:1 fit for declarative charts, incl. regression w/
 *  CI, density, window/rolling, binning); the 4 composition types (treemap,
 *  sunburst, venn, sankey) emit full Vega specs (VegaEmbed auto-detects via
 *  $schema, so the PlotSpec `kind` stays 'vega'). */
export type ChartType =
  // time series
  | 'line' | 'area' | 'stepLine' | 'rollingLine'
  // snapshot / categorical
  | 'bar' | 'barOverTime' | 'groupedBar' | 'pie' | 'funnel'
  // distribution
  | 'histogram' | 'box' | 'violin' | 'density'
  // relationship
  | 'scatter' | 'regression' | 'splom' | 'heatmap' | 'correlation'
  // composition / set
  | 'treemap' | 'sunburst' | 'venn' | 'sankey'
  // indicators
  | 'kpi' | 'bullet'
  // tables
  | 'table';

/** One comparison in a chart's filter transform. `field` resolves per tick like
 *  any FieldRef; `value` is a literal. */
export interface ChartFilter {
  field: FieldRef;
  op: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'in';
  value: number | string | (string | number)[];
}

/** Field mappings for a chart. `y` accepts a single ref or an array (multi-
 *  series — one trace per ref, melted into a `__series` column by the builder).
 *  The optional channels are surfaced by the editor only when the chart type
 *  declares them in its required/optional encoding list. */
export interface ChartEncoding {
  x?: FieldRef;
  y: FieldRef | FieldRef[];
  color?: FieldRef;
  size?: FieldRef;
  facet?: FieldRef;
}

/** Pre-recipe transforms the builder applies to the extracted rows. Vega-Lite
 *  handles aggregate/bin/window/regression/density natively when it can; the
 *  TS pre-compute steps are only for things Vega-Lite can't (correlation matrix
 *  math, venn set overlap). */
export interface ChartTransform {
  aggregate?: 'sum' | 'mean' | 'median' | 'min' | 'max' | 'count' | 'std';
  groupBy?: FieldRef[];
  filter?: ChartFilter[];
  window?:
    | { type: 'rolling'; size: number; stat?: 'mean' | 'sum' | 'std' }
    | { type: 'cumulative' };
  regression?: {
    method: 'linear' | 'log' | 'exp' | 'pow' | 'quad' | 'poly';
    order?: number;
    /** Confidence-interval width, e.g. 0.95. When set, the recipe layers a
     *  CI band on top of the fit line. */
    ci?: number;
  };
}

/** A no-code chart specification — persisted as `charts/<id>.json`. */
export interface ChartSpec {
  id: string;
  label: string;
  description?: string;
  refresh: 'tick' | 'ondemand' | 'manual';
  chartType: ChartType;
  /** Where the rows come from. `live` melts the in-memory recent-tick buffer;
   *  `sql` runs a query through the project's sql seam (the query does the
   *  aggregation — GROUP BY — and rows are addressed via `column` FieldRefs);
   *  `view` reads one of the project's reporting views (the aggregation lives
   *  IN the view; reads go through the records view endpoint). */
  dataSource:
    | { kind: 'live' }
    | { kind: 'sql'; query: string }
    | { kind: 'view'; table: string; view: string; filter?: string; sort?: string };
  encoding: ChartEncoding;
  transform?: ChartTransform;
  style?: {
    title?: string;
    xLabel?: string;
    yLabel?: string;
    /** Show/hide the legend. Default true. */
    legend?: boolean;
  };
}

/** Lightweight index entry for `GET /api/projects/{id}/charts`. The full
 *  `ChartSpec` is fetched on demand via `readFile('charts/<id>.json')`. */
export interface ChartEntry {
  id: string;
  label: string;
  chartType: ChartType;
  refresh: 'tick' | 'ondemand' | 'manual';
}

// ─── Model state (engine-internal, derived from bundle + ticks) ────────────
export interface ModelState {
  step: number;
  stocks: Record<NodeId, number>;
  /** Latest applied tick, kept so the UI can render the current frame and the
   *  engine can compute the next stock update. */
  lastTick: TickPayload;
}
